 import React from "react";
import { useState, useEffect } from "react";
import { fromJS } from "immutable";
import MAP_STYLE from "./Map1Style.json";
import {Button, ButtonGroup, Radio, RadioGroup, FormControlLabel, Slider, Tooltip, } from "@material-ui/core/";
import { withStyles } from "@material-ui/styles";
import InfoIcon from "@material-ui/icons/Info";


const defaultMapStyle = fromJS(MAP_STYLE);
const defaultLayers = defaultMapStyle.get("layers");

const PrettoSlider = withStyles({
  root: {
    color: "rgb(190, 215, 71)",
    height: 1,
    paddingTop: "5px",
    paddingBottom: "10px" 
  },
  thumb: {
    height: 18,
    width: 18,
    backgroundColor: "#fff",
    border: "2px solid currentColor",
    marginTop: -6,
    marginLeft: -10,
    "&:focus, &:hover, &$active": {
      boxShadow: "inherit",
    },
  },
  active: {},
  valueLabel: {
    left: "calc(-50%)",
  },
  track: {
    height: 6,
    borderRadius: 4,
  },
  rail: {
    height: 6,
    borderRadius: 4,
  },
})(Slider);

const LayerTooltip = withStyles({
  tooltipPlacementRight: {
    left: "7px",
  },
})(Tooltip);

function changeTheMap(activeStyle, sliderValue, activeLayer) {
  let layers = defaultLayers.filter((layer) => {
    const style = layer.getIn(["metadata", "style"]);
    const id = layer.get("id");
    return style === activeStyle || id === activeLayer;
  });
  layers = layers.map((layer) => {
    const style = layer.getIn(["metadata", "style"]);
    const type = layer.get("type");
    const newPaintValue = sliderValue * 0.01;
    if (style === "none" && type === "raster") {
      layer = layer.setIn(["paint", "raster-opacity"], newPaintValue);
    }
    return layer;
  });
  return defaultMapStyle.set("layers", layers);
}

const Map1Control = (props) => {
  const [activeStyle, setActiveStyle] = useState("darkSimple");
  const [sliderValue, setSliderValue] = useState(100);
  const activeLayer = props.activeLayer;
  const setActiveLayer = props.setActiveLayer; 

  useEffect(() => {
    props.onChange(changeTheMap(activeStyle, sliderValue, activeLayer));
  }, [activeStyle, sliderValue, activeLayer]);

  return (
    <div>
      <div className="styleButtons">
        <ButtonGroup
          variant="contained"
          color="primary"
          aria-label="contained primary button group"
        >
          <Button
            value={"satellite"}
            onClick={(e) => setActiveStyle(e.currentTarget.value)}
          >
            Satellite
          </Button>
          <Button
            value={"darkSimple"}
            onClick={(e) => setActiveStyle(e.currentTarget.value)}
          >
            Dark
          </Button>
          <Button
            value={"light"}
            onClick={(e) => setActiveStyle(e.currentTarget.value)}
          >
            Light
          </Button>
        </ButtonGroup>
      </div>

      <div className="control-panel">
          <p className="title">Species Movement</p>
          <RadioGroup
            aria-label="Wildlife Connectivity Map Layers"
            name="Wildlife Connectivity Map Layers"
            value={activeLayer}
            onChange={(evt) => setActiveLayer(evt.target.value)}
          >
            <LayerTooltip placement="right" title={<><p noGutter style={{fontSize: "12.5px", zIndex: 50, padding: "3px"}}> Ability of a given location to support the long-distance movement of organisms (e.g., through migration) and to provide linkages between areas of high quality habitat.</p></>} arrow>
                <FormControlLabel
                  className="formControlLabel"
                  value="Connectivity"
                  control={<Radio />}
                  label="Connectivity"
              />
            </LayerTooltip>

            <LayerTooltip placement="right" title={<><p style={{fontSize: "12.5px", zIndex: 50, padding: "3px"}}> Difficulty that an organism experiences in moving through a location on the landscape. More modified landscapes (i.e., heavily developed areas or those under intensive agriculture) have higher resistance.</p> </>} arrow>
              <FormControlLabel
                className="formControlLabel"
                value="Landscape Resistance"
                control={<Radio />}
                label="Landscape Resistance"
              />
            </LayerTooltip>
          </RadioGroup>
      </div>

      <div className="control-panel2">
          <p className="title">Agriculture</p>
          <RadioGroup
            aria-label="Agricultural Map Layers"
            name="Agricultural Map Layers"
            value={activeLayer}
            onChange={(evt) => setActiveLayer(evt.target.value)}
          >

          <LayerTooltip placement="right" title={<><p style={{fontSize: "12.5px", zIndex: 50, padding: "3px"}}> Type of agriculture practiced on working lands across the U.S., circa 2016.</p> </>} arrow>
            <FormControlLabel
              className="formControlLabel"
              value="Agricultural Land Cover"
              control={<Radio />}
              label="Agricultural Land Cover"
              id="Agricultural Land Cover"
            />
            </LayerTooltip>

          <LayerTooltip placement="right" title={<><p style={{fontSize: "12.5px", zIndex: 50, padding: "3px"}}> Long-term sustainability of maintaining a given area in cultivation based on soil productivity, land cover and use, and food production. PVR is a general measure of the importance of keeping a given location in agriculture..</p> </>} arrow>
            <FormControlLabel
              className="formControlLabel"
              value="PVR"
              control={<Radio />}
              label="Productivite, Versatile, and Resilient (PVR) lands"
            />
            </LayerTooltip> 
          </RadioGroup>
      </div>

      <div className="control-panel3">
          <p className="title">Co-benefits for Connectivity and Agriculture</p>
          <RadioGroup
            aria-label="Agricultural Map Layers"
            name="Agricultural Map Layers"
            value={activeLayer}
            onChange={(evt) => setActiveLayer(evt.target.value)}
          >

            <LayerTooltip placement="right" title={<><p style={{fontSize: "12.5px", zIndex: 50, padding: "3px"}}>Importance of a given location for both connectivity and food production. Each location on agricultural lands is ranked as low, medium, or high for connectivity and PVR relative to other agricultural lands. These rankings can be used to identify potential conservation opportunities on agricultural lands, as described in the {<InfoIcon />}.</p> </>} arrow>
            <FormControlLabel
              className="formControlLabel"
              value="Conservation Opportunity"
              control={<Radio />}
              label="Connectivity and PVR Rankings"
            />
            </LayerTooltip>

            <LayerTooltip placement="right" title={<><p style={{fontSize: "12.5px", zIndex: 50, padding: "3px"}}> U.S. counties color-coded by the most common connectivity and PVR ranking across all agricultural lands within the county. Counties in black have low amounts of agricultural land.</p> </>} arrow>
            <FormControlLabel
              className="formControlLabel"
              value="County-level Values"
              control={<Radio />}
              label="County Summaries"
            />
            </LayerTooltip>

          </RadioGroup>
      </div>


      <div className="map1-opacity">
        <label>
          Opacity: <span id="slider-value">{sliderValue}%</span>
        </label>
        <PrettoSlider
          id="slider"
          min={0}
          max={100}
          step={1}
          value={sliderValue}
          onChange={(event, value) => setSliderValue(value)}
        />
      </div>
    </div>
  );
};

export default React.memo(Map1Control);
